import { useEffect, useState } from 'react';
import type { ServerEvent } from '@converoom/contracts';
import { api } from '../lib/api';
import type { Realtime, ConnState } from '../lib/realtime';
import type { Me, Project, Room } from '../lib/types';
import { TeamChat } from '../components/TeamChat';
import { NotificationOptIn, NudgeSoundPicker } from '../components/Nudge';
import { dateTime, PROVIDER_NAME, RUN_STATE_TEXT, usd } from '../lib/format';
import { navigate } from '../lib/router';

const TABS = [
  ['team', 'Ekip'],
  ['tasks', 'Görevler'],
  ['decisions', 'Kararlar'],
  ['files', 'Dosyalar'],
  ['runs', 'Çağrılar'],
] as const;
type Tab = (typeof TABS)[number][0];

interface Props {
  me: Me;
  project: Project;
  room: Room;
  rt: Realtime;
  conn: ConnState;
  onClose: () => void;
}

export function Drawer({ me, project, room, rt, conn, onClose }: Props) {
  const [tab, setTab] = useState<Tab>('team');
  return (
    <>
      <div className="tabs-bar">
        <div className="tabs" role="tablist" aria-label="Proje paneli sekmeleri">
          {TABS.map(([id, label]) => (
            <button
              key={id}
              role="tab"
              id={`tab-${id}`}
              aria-controls={`panel-${id}`}
              aria-selected={tab === id}
              className="tab"
              onClick={() => setTab(id)}
            >
              {label}
            </button>
          ))}
        </div>
        <button
          className="btn small ghost"
          style={{ marginLeft: 'auto', alignSelf: 'center', flex: 'none' }}
          onClick={onClose}
          aria-label="Paneli kapat"
        >
          Kapat
        </button>
      </div>
      <div className="panel" role="tabpanel" id={`panel-${tab}`} aria-labelledby={`tab-${tab}`}>
        {/* TeamChat stays mounted across tabs so the in-memory session is not lost by switching tabs. */}
        <div
          hidden={tab !== 'team'}
          style={{
            display: tab === 'team' ? 'flex' : 'none',
            flexDirection: 'column',
            gap: 'var(--s3)',
            flex: 1,
            minHeight: 0,
          }}
        >
          <PresenceList projectId={project.id} roomId={room.id} rt={rt} me={me} conn={conn} />
          <TeamChat key={project.id} projectId={project.id} rt={rt} conn={conn} me={me} />
        </div>
        {tab === 'tasks' && <Tasks project={project} room={room} />}
        {tab === 'decisions' && <Decisions project={project} room={room} />}
        {tab === 'files' && <Files room={room} />}
        {tab === 'runs' && <Runs room={room} me={me} />}
      </div>
    </>
  );
}

const NUDGE_RESULT: Record<string, string> = {
  sent: 'titreşim gönderildi',
  offline: 'şu an bağlı değil',
  rate_limited: 'az önce titrettiniz — 20 sn bekleyin',
  no_room_access: 'bu odayı göremiyor',
  forbidden: 'titreşim gönderilemedi (yetki yok)',
  self: 'kendinizi titretemezsiniz',
};

function PresenceList({
  projectId,
  roomId,
  rt,
  me,
  conn,
}: {
  projectId: string;
  roomId: string;
  rt: Realtime;
  me: Me;
  conn: ConnState;
}) {
  const [members, setMembers] = useState<{ userId: string; name: string; state: string }[]>([]);
  const [results, setResults] = useState<Record<string, { text: string; ok: boolean }>>({});
  useEffect(() => {
    setMembers(rt.presence.get(projectId) ?? []);
    const timers: number[] = [];
    const off = rt.on((e: ServerEvent) => {
      if (e.t === 'presence' && e.projectId === projectId) setMembers(e.members);
      else if (e.t === 'nudge.result') {
        setResults((r) => ({
          ...r,
          [e.toUserId]: { text: NUDGE_RESULT[e.status] ?? e.status, ok: e.status === 'sent' },
        }));
        timers.push(
          window.setTimeout(
            () =>
              setResults((r) => {
                const n = { ...r };
                delete n[e.toUserId];
                return n;
              }),
            5_000,
          ),
        );
      }
    });
    return () => {
      off();
      timers.forEach((t) => window.clearTimeout(t));
    };
  }, [projectId, rt]);
  const nudge = (toUserId: string) => {
    if (!rt.send({ t: 'nudge.send', projectId, roomId, toUserId }))
      setResults((r) => ({ ...r, [toUserId]: { text: 'bağlantı yok', ok: false } }));
  };
  return (
    <section aria-labelledby="presence-title">
      <h2 id="presence-title" style={{ fontSize: 14, margin: '0 0 var(--s2)', fontWeight: 600 }}>
        Bu projede bağlı olanlar
      </h2>
      {members.length === 0 ? (
        <p className="hint">Doğrulanmış bağlantı yok.</p>
      ) : (
        <ul className="presence">
          {members.map((m) => (
            <li key={m.userId} style={{ flexWrap: 'wrap' }}>
              <span
                className={`dot ${m.state === 'reconnecting' ? 'reconnecting' : ''}`}
                aria-hidden="true"
              />
              {m.name}
              {m.userId === me.id && ' (siz)'}
              <span className="hint" style={{ marginLeft: 'auto' }}>
                {m.state === 'online' ? 'bağlı' : 'bağlantı doğrulanıyor'}
              </span>
              {m.userId !== me.id && m.state === 'online' && (
                <button
                  className="btn small ghost"
                  onClick={() => nudge(m.userId)}
                  disabled={conn !== 'open'}
                  aria-label={`${m.name} adlı kişiye titreşim gönder`}
                  title="Bu odada seni beklediğini bildir"
                >
                  📳 Titret
                </button>
              )}
              {results[m.userId] && (
                <span
                  role="status"
                  className={results[m.userId]!.ok ? 'hint' : 'error-text'}
                  style={{ flexBasis: '100%', fontSize: 12 }}
                >
                  {m.name}: {results[m.userId]!.text}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
      <p className="hint" style={{ marginTop: 'var(--s1)' }}>
        Bağlı olmak, mesajı okuduğu anlamına gelmez. Titret: bağlı ekip arkadaşına bu odada onu beklediğinizi
        bildirir.
      </p>
      <NotificationOptIn />
      <NudgeSoundPicker />
    </section>
  );
}

function useLoad<T>(url: string, deps: unknown[] = []) {
  const [data, setData] = useState<T | null>(null);
  const [n, setN] = useState(0);
  useEffect(() => {
    let alive = true;
    api
      .get<T>(url)
      .then((d) => alive && setData(d))
      .catch(() => alive && setData(null));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, n, ...deps]);
  return [data, () => setN((x) => x + 1)] as const;
}

interface Task {
  id: string;
  number: number;
  title: string;
  status: string;
  branch: string | null;
  links: { kind: string; ref: string }[];
}
const TASK_STATUS: Record<string, string> = {
  open: 'Açık',
  in_progress: 'Sürüyor',
  review: 'İncelemede',
  done: 'Bitti',
  cancelled: 'İptal',
};

function Tasks({ project, room }: { project: Project; room: Room }) {
  const [tasks, reload] = useLoad<Task[]>(`/v1/projects/${project.id}/tasks`);
  const [title, setTitle] = useState('');
  return (
    <>
      {project.role !== 'guest' && (
        <form
          style={{ display: 'flex', gap: 'var(--s2)' }}
          onSubmit={async (e) => {
            e.preventDefault();
            if (!title.trim()) return;
            await api.post(`/v1/projects/${project.id}/tasks`, { title: title.trim(), roomId: room.id });
            setTitle('');
            reload();
          }}
        >
          <input
            className="input"
            aria-label="Yeni görev başlığı"
            placeholder="Yeni görev"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
          <button className="btn" type="submit">
            Ekle
          </button>
        </form>
      )}
      {tasks?.length === 0 && (
        <p className="hint">Görev yok. Görevler; karar, commit, PR ve test kayıtlarıyla bağlanır.</p>
      )}
      <ul className="list">
        {tasks?.map((t) => (
          <li key={t.id}>
            <div>
              <strong>CR-TASK-{t.number}</strong> {t.title}
            </div>
            <div className="meta">
              <select
                className="select"
                style={{ width: 'auto', minHeight: 28, padding: '0 var(--s2)' }}
                aria-label={`CR-TASK-${t.number} durumu`}
                value={t.status}
                onChange={async (e) => {
                  await api.patch(`/v1/tasks/${t.id}`, { status: e.target.value });
                  reload();
                }}
              >
                {Object.entries(TASK_STATUS).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </select>
              {t.branch && (
                <>
                  {' '}
                  dal: <code>{t.branch}</code>
                </>
              )}
            </div>
            {t.links.length > 0 && (
              <ul className="meta" style={{ margin: 'var(--s1) 0 0', paddingLeft: 'var(--s4)' }}>
                {t.links.map((l) => (
                  <li key={l.kind + l.ref} style={{ border: 0, padding: 0 }}>
                    {l.kind}: {l.ref}
                  </li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ul>
    </>
  );
}

interface Decision {
  id: string;
  number: number;
  title: string;
  body: string;
  status: string;
  revision: number;
  createdBy: string;
  createdAt: string;
  supersedesId: string | null;
}

function Decisions({ project, room }: { project: Project; room: Room }) {
  const [items, reload] = useLoad<Decision[]>(`/v1/projects/${project.id}/decisions`);
  const [draft, setDraft] = useState({ title: '', body: '', scope: 'project' });
  return (
    <>
      <p className="hint">
        Kabul edilen kararlar, bu projedeki AI çağrılarının bağlamına eklenir. Kararlar düzenlenmez; yenisi
        eskisinin yerini alır.
      </p>
      {project.role !== 'guest' && (
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            await api.post(`/v1/projects/${project.id}/decisions`, {
              title: draft.title,
              body: draft.body,
              status: 'accepted',
              ...(draft.scope === 'room' ? { roomId: room.id } : {}),
            });
            setDraft({ title: '', body: '', scope: 'project' });
            reload();
          }}
        >
          <label className="field">
            <span>Başlık</span>
            <input
              className="input"
              required
              value={draft.title}
              onChange={(e) => setDraft({ ...draft, title: e.target.value })}
            />
          </label>
          <label className="field">
            <span>Karar</span>
            <textarea
              className="textarea"
              rows={3}
              required
              value={draft.body}
              onChange={(e) => setDraft({ ...draft, body: e.target.value })}
            />
          </label>
          <label className="field">
            <span>Görünürlük</span>
            <select
              className="select"
              value={draft.scope}
              onChange={(e) => setDraft({ ...draft, scope: e.target.value })}
            >
              <option value="project">Tüm proje</option>
              <option value="room">Yalnızca bu oda ({room.name})</option>
            </select>
          </label>
          <button className="btn" type="submit">
            Kararı kaydet
          </button>
        </form>
      )}
      <ul className="list">
        {items?.map((d) => (
          <li key={d.id}>
            <div>
              <strong>DEC-{d.number}</strong> {d.title}{' '}
              {d.status !== 'accepted' && (
                <span className="hint">
                  ({d.status === 'superseded' ? 'yerini başka karar aldı' : 'öneri'})
                </span>
              )}
            </div>
            <div style={{ fontSize: 14 }}>{d.body}</div>
            <div className="meta">
              {d.createdBy} — {dateTime(d.createdAt)} — sürüm {d.revision}
            </div>
          </li>
        ))}
      </ul>
    </>
  );
}

function Files({ room }: { room: Room }) {
  const [files] = useLoad<
    { id: string; name: string; mime: string; sizeBytes: number; uploader: string; createdAt: string }[]
  >(`/v1/rooms/${room.id}/files`);
  return (
    <>
      {files?.length === 0 && (
        <p className="hint">Bu odada dosya yok. Mesaj kutusundaki “Dosya ekle” ile yükleyebilirsiniz.</p>
      )}
      <ul className="list">
        {files?.map((f) => (
          <li key={f.id}>
            <a href={`/v1/files/${f.id}`}>{f.name}</a>
            <div className="meta">
              {f.uploader} — {(f.sizeBytes / 1024).toFixed(1)} KB — {dateTime(f.createdAt)}
            </div>
          </li>
        ))}
      </ul>
    </>
  );
}

function Runs({ room, me }: { room: Room; me: Me }) {
  const [runs] = useLoad<
    {
      id: string;
      provider: string;
      model: string;
      state: string;
      blockReason: string | null;
      payerUserId: string;
      estimatedCostMicros: number | null;
      createdAt: string;
    }[]
  >(`/v1/rooms/${room.id}/runs`);
  return (
    <>
      <p className="hint">
        Her çağrı başlatan kişinin kendi bağlantısıyla yapılır. Başkalarının harcama tutarları size
        gösterilmez.
      </p>
      <ul className="list">
        {runs?.map((r) => (
          <li key={r.id}>
            <div>
              {PROVIDER_NAME[r.provider] ?? r.provider} <span className="msg-model">{r.model}</span>
            </div>
            <div className="meta">
              {RUN_STATE_TEXT[r.state] ?? r.state}
              {r.blockReason && ` (${r.blockReason})`} —{' '}
              {r.payerUserId === me.id ? `sizin — tahmini ${usd(r.estimatedCostMicros)}` : 'başka üye'} —{' '}
              {dateTime(r.createdAt)}
            </div>
          </li>
        ))}
      </ul>
      <button className="btn small" onClick={() => navigate('/settings/usage')}>
        Kişisel kullanımım
      </button>
    </>
  );
}
